import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { appsDb, linksDb, notesDb, projectsDb, questionsDb, tipsDb } from '../../lib/db/index.js';
import { useAuth } from '../../lib/firebase/auth-context.js';
import { useUser } from '../../lib/firebase/use-user.js';
import { PageHeader } from '../../components/shared/PageHeader.js';
import { Spinner } from '../../components/shared/Spinner.js';
import { EmptyState } from '../../components/shared/EmptyState.js';
import { Icon } from '../../components/shared/Icon.js';
import type { Project } from '../../types/project.js';

interface Counts {
  links: number;
  questions: number;
  notes: number;
  apps: number;
  tips: number;
}

async function loadProjectCounts(): Promise<Map<string, Counts>> {
  const [links, questions, notes, apps, tips] = await Promise.all([
    linksDb.findShared({ count: 500 }),
    questionsDb.findShared({ count: 500 }),
    notesDb.findShared({ count: 500 }),
    appsDb.findShared({ count: 500 }),
    tipsDb.findShared({ count: 500 }),
  ]);
  const map = new Map<string, Counts>();
  const tally = (id: string | undefined, key: keyof Counts) => {
    if (!id) return;
    const c = map.get(id) ?? { links: 0, questions: 0, notes: 0, apps: 0, tips: 0 };
    c[key] += 1;
    map.set(id, c);
  };
  links.forEach((x) => tally(x.projectId, 'links'));
  questions.forEach((x) => tally(x.projectId, 'questions'));
  notes.forEach((x) => tally(x.projectId, 'notes'));
  apps.forEach((x) => tally(x.projectId, 'apps'));
  tips.forEach((x) => tally(x.projectId, 'tips'));
  return map;
}

export function ProjectsPage() {
  const { fbUser } = useAuth();
  const [params, setParams] = useSearchParams();

  const projectsQ = useQuery({
    queryKey: ['projects', 'shared'],
    queryFn: () => projectsDb.findShared({ count: 200 }),
    staleTime: 5 * 60_000,
  });
  const countsQ = useQuery({
    queryKey: ['projects', 'counts'],
    queryFn: loadProjectCounts,
    staleTime: 5 * 60_000,
  });

  const items = projectsQ.data ?? [];

  const openCreate = () => {
    const next = new URLSearchParams(params);
    next.set('compose', 'project');
    setParams(next);
  };

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <PageHeader
          eyebrow="05 · TOPICS"
          title="トピック"
          subtitle="URL・質問・検証メモ・作成アプリをトピック単位でまとめて把握する。"
        />
        {fbUser && (
          <button type="button" className="btn btn-primary sm" onClick={openCreate}>
            <Icon name="plus" size={12} />
            トピックを作成
          </button>
        )}
      </div>

      {projectsQ.isPending && <Spinner />}
      {projectsQ.data && items.length === 0 && (
        <EmptyState title="共有トピックはまだありません" />
      )}
      {items.length > 0 && (
        <div className="project-grid">
          {items.map((p) => (
            <ProjectCard
              key={p.id}
              project={p}
              counts={countsQ.data?.get(p.id) ?? { links: p.links ?? 0, questions: p.questions ?? 0, notes: p.notes ?? 0, apps: p.apps ?? 0, tips: p.tips ?? 0 }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectCard({ project, counts }: { project: Project; counts: Counts }) {
  const nav = useNavigate();
  const owner = useUser(project.createdBy);

  return (
    <div className="project-card" onClick={() => nav(`/projects/${project.id}`)}>
      <div className="project-card-head">
        <div className="project-swatch" style={{ background: project.color }} />
        <div className="project-name">{project.name}</div>
      </div>
      <div className="project-desc">{project.description}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span className="badge" data-status={project.status}>
          <span className="badge-dot" />
          {project.status}
        </span>
        {owner.data && (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--ink-3)' }}>
            オーナー: {owner.data.name}
          </span>
        )}
      </div>
      <div className="project-stats">
        <div className="project-stat"><div className="p-num">{counts.links}</div><div className="p-lbl">URL</div></div>
        <div className="project-stat"><div className="p-num">{counts.questions}</div><div className="p-lbl">Q&amp;A</div></div>
        <div className="project-stat"><div className="p-num">{counts.tips}</div><div className="p-lbl">TIPS</div></div>
        <div className="project-stat"><div className="p-num">{counts.notes}</div><div className="p-lbl">メモ</div></div>
        <div className="project-stat"><div className="p-num">{counts.apps}</div><div className="p-lbl">アプリ</div></div>
      </div>
    </div>
  );
}
